import {
  daysUntil,
  DOCUMENT_WARNING_DAYS,
  roleAtLeast,
  type OperatorDocument,
  type OperatorPermission,
  type OperatorStatus,
  type TenantRole,
} from "@fts/core";
import type { AuthlistStatus } from "../data/provider";

/** Operators, cards, documents and permissions are managed by safety (İSG) and above. */
export const canManageOperators = (role: TenantRole | null): boolean => !!role && roleAtLeast(role, "safety");

export const OPERATOR_STATUS_TONE: Record<OperatorStatus, string> = {
  active: "ok",
  expiring: "warn",
  suspended: "crit",
  inactive: "neutral",
  left: "neutral",
};

/** Delivery state of a device's card list as the console shows it. */
export type AuthlistState = "synced" | "pending" | "error" | "none";

export function authlistState(s: AuthlistStatus | undefined): AuthlistState {
  if (!s) return "none";
  if (s.ackedVer === s.ver) return "synced";
  if (s.error) return "error";
  return "pending";
}

export const AUTHLIST_TONE: Record<AuthlistState, string> = { synced: "ok", pending: "warn", error: "crit", none: "neutral" };

/** Warning level of a document: expired, or the tightest of the 30/15/7-day thresholds it is within. */
export type DocumentWarning = "expired" | 7 | 15 | 30 | null;

export function documentWarning(d: Pick<OperatorDocument, "validUntil">, today: string): DocumentWarning {
  const left = daysUntil(d.validUntil, today);
  if (left < 0) return "expired";
  const hit = [...DOCUMENT_WARNING_DAYS].sort((a, b) => a - b).find((n) => left <= n);
  return (hit as 7 | 15 | 30 | undefined) ?? null;
}

export function permissionExpired(p: Pick<OperatorPermission, "validUntil">, today: string): boolean {
  return p.validUntil !== null && p.validUntil < today;
}

/** Date input value (YYYY-MM-DD) n days from `today`. */
export function addDays(today: string, n: number): string {
  return new Date(Date.parse(`${today}T00:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10);
}

/** Display a YYYY-MM-DD business date without time-zone shifts. */
export function formatDay(day: string | null, locale: string): string | null {
  if (!day) return null;
  const d = new Date(`${day}T12:00:00Z`);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString(locale, { day: "2-digit", month: "short", year: "numeric", timeZone: "UTC" });
}
